import { Grid, Typography } from "@material-ui/core";
import DropdownFilter from "components/DropdownFilter";
import EmptyPlaceholder from "components/EmptyPlaceholder/EmptyPlaceholder";
import LotsDropdown from "components/SharedDropdowns/LotsDropDown";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { selectTenantName } from "features/user/userSlice";
import { useGetVehiclesQuery } from "features/vehicles/vehicleSlice";
import { useGetStatusesForClientQuery } from "features/vehicles/VehicleStatus/vehicleStatusSlice";
import { IconCar, IconLot, IconPmfLogo } from "icons";
import { groupBy, sortBy } from "lodash";
import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import FilterService from "services/filterService";
import { theme } from "styles/theme";
import { arrayToObject } from "utils/object-util";
import WidgetTitle from "../WidgetTitle/WidgetTitle";
import { dwellWidgetStyle } from "./dwellWidgetStyle";
import { timeDwelling, timeDwellingObj, timeDwellingGroups } from "./DwellConsts";
import { AssetDwellListDialog } from "./AssetDwellListDialog";

const DwellWidget = () => {
    const classes = dwellWidgetStyle();
    const tenantName = useSelector(selectTenantName);

    let { data: vehiclesData, error: vehicleError, isLoading: isLoadingVehicles } = useGetVehiclesQuery({ includeHistory: false });
    let { data: lots, error: lotError, isLoading: isLoadingLots } = useGetLotsQuery();
    let { data: clientVehicleStatuses, error: clientVehicleStatusesError, loading: clientVehicleStatusesLoading } = useGetStatusesForClientQuery({ clientId: 0 });

    vehiclesData = vehiclesData || [];
    lots = lots || [];
    clientVehicleStatuses = clientVehicleStatuses?.filter(s => s.onLot) || [];

    const { dashboardFilters } = useSelector(state => state.fleetManagementDashboardState);
    const loading = isLoadingVehicles || isLoadingLots || clientVehicleStatusesLoading;
    const [tenantLotFilter, setTenantLotFilter] = useState(1);

    const [filters, setFilters] = useState({
        'lotId': { value: [] },
        'vehicleStatusId': { value: [] }
    });

    const onDropFilter = (filterValue, prop) => {
        setFilters({ ...filters, [prop]: { value: filterValue } });
    };

    const filteredData = useMemo(() => {
        const base = FilterService.filter(filters, null, vehiclesData, []) || [];
        return base.filter(f =>
            dashboardFilters.isDefaultAccount
                ? (!dashboardFilters.clientId || dashboardFilters.clientId == f?.clientId)
                : (!dashboardFilters.clientAccountId || dashboardFilters.clientAccountId == f?.clientAccountId)
        );
    }, [vehiclesData, filters, dashboardFilters]);

    const groupedByLots = useMemo(() => groupBy(filteredData, 'lotId'), [filteredData]);
    const lotsLookup = useMemo(() => arrayToObject(lots), [lots]);

    const finalData = useMemo(() => {
        const rows = Object.keys(groupedByLots).map(key => {
            const lotVehicles = groupedByLots[key] || [];

            let lessThan30Days = 0;
            let moreThan30Days = 0;
            let moreThan60Days = 0;

            for (const v of lotVehicles) {
                const d = v.daysOnLot;
                if (d < 30) {
                    lessThan30Days++;
                } else if (d > 60) {
                    moreThan60Days++;
                } else {
                    moreThan30Days++;
                }
            }

            return {
                lotId: +key,
                lotName: lotsLookup?.[key]?.name,
                ownedByTenant: lotsLookup?.[key]?.ownedByTenant ? 1 : 2,
                count: lotVehicles.length,
                [timeDwellingGroups.LESS_THAN_30_DAYS]: lessThan30Days,
                [timeDwellingGroups.MORE_THAN_30_DAYS]: moreThan30Days,
                [timeDwellingGroups.MORE_THAN_60_DAYS]: moreThan60Days
            };
        });

        const filtered = rows.filter(item => tenantLotFilter ? item?.ownedByTenant == tenantLotFilter : true);
        return sortBy(filtered, [d => d.lotName]);
    }, [groupedByLots, lotsLookup, tenantLotFilter]);

    const CustomTooltip = ({ payload }) => {
        if (!payload?.[0]) return null;
        const p = payload[0].payload;
        const lot = lotsLookup?.[p?.lotId];
        return (
            <Grid container direction="column" className={classes.graphTootltip} spacing={0.5} >
                <Grid item>
                    <Grid container direction="column" spacing={0}>
                        <Grid item style={{ fontSize: 12 }}>Lot:</Grid>
                        <Grid item style={{ fontSize: 10 }}>
                            <strong>{`${lot?.name ?? ""} ${lot?.addressLine1 ?? ""}`}</strong>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    item
                    style={{
                        color: timeDwellingObj[timeDwellingGroups.LESS_THAN_30_DAYS].color,
                        fontSize: 12
                    }}
                >
                    <strong>{p?.[timeDwellingGroups.LESS_THAN_30_DAYS]} assets</strong>
                    <span>&nbsp;{"< 30 days"}</span>
                </Grid>
                <Grid
                    item
                    style={{
                        color: timeDwellingObj[timeDwellingGroups.MORE_THAN_30_DAYS].color,
                        fontSize: 11
                    }}
                >
                    <strong>{p?.[timeDwellingGroups.MORE_THAN_30_DAYS]} assets</strong>
                    <span>&nbsp;{"30 – 60 days"}</span>
                </Grid>
                <Grid
                    item
                    style={{
                        color: timeDwellingObj[timeDwellingGroups.MORE_THAN_60_DAYS].color,
                        fontSize: 12
                    }}
                >
                    <strong>{p?.[timeDwellingGroups.MORE_THAN_60_DAYS]} assets</strong>
                    <span>&nbsp;{"> 60 days"}</span>
                </Grid>
            </Grid>

        );
    };

    const mapExportData = (a => {
        return {
            ['Lot']: a.lotName,
            ['Asset < 30 days']: a.lessThan30Days,
            ['Asset 30-60 days']: a.moreThan30Days,
            ['Asset > 60 days']: a.moreThan60Days,
            ['Total Assets']: a.count,
        };
    });

    return (
        <Grid container direction="column">
            <Grid container direction='row'>
                <Grid item xs={11} >
                    <WidgetTitle
                        title='Dwell Report'
                        loading={loading}
                        filtersList={{ ...filters, 'tenantLotFilter': { value: tenantLotFilter } }}
                        mapExportData={mapExportData}
                        dataToExport={finalData}
                        filters={
                            <Grid container spacing={1}>
                                <Grid className={classes.filterIcon} item xs={2}>
                                    <IconPmfLogo height={17} fill="#0090fe" />
                                </Grid>
                                <Grid item xs={10}>
                                    <DropdownFilter
                                        identifier="ownedByTenant"
                                        value={tenantLotFilter}
                                        onSelect={(val) => setTenantLotFilter(val)}
                                        values={[
                                            { id: 1, name: `${tenantName} Lots` },
                                            { id: 2, name: `Non ${tenantName} Lots` },
                                        ]}
                                        showEmpty={true}
                                    />
                                </Grid>
                                <Grid className={classes.filterIcon} item xs={2}>
                                    <IconLot />
                                </Grid>
                                <Grid item xs={10}>
                                    <LotsDropdown
                                        title='Lot'
                                        identifier='lot'
                                        values={lots}
                                        onSelect={(val) => onDropFilter(val, 'lotId')}
                                        value={filters?.['lotId']?.value}
                                        showEmpty={true}
                                        multiple={true}
                                        ownedByTenant={tenantLotFilter == 1}
                                    />
                                </Grid>
                                <Grid className={classes.filterIcon} item xs={2}>
                                    <IconCar width={20} />
                                </Grid>
                                <Grid item xs={10}>
                                    <DropdownFilter
                                        title="Status"
                                        identifier="status"
                                        value={filters?.['vehicleStatusId']?.value}
                                        onSelect={(val) => onDropFilter(val, 'vehicleStatusId')}
                                        values={clientVehicleStatuses ?? []}
                                        showEmpty={true}
                                        multiple={true}
                                    />
                                </Grid>
                            </Grid>
                        } />
                </Grid>
                <Grid item xs={1} className={classes.tableIconWrapper}>
                    <AssetDwellListDialog data={vehiclesData} title={'Dwell Assets'} />
                </Grid>
            </Grid>
            <Grid item style={{ height: 400 }}>
                {!finalData?.length ? (
                    <EmptyPlaceholder text='No Assets' />
                ) : finalData.length === 1 ? (
                    <div className={classes.singleLotCard}>
                        <Typography className={classes.singleLotCount}>{finalData[0].count}</Typography>
                        <Typography className={classes.singleLotLabel}>Assets at {finalData[0].lotName}</Typography>
                        <div className={classes.singleLotDivider} />
                        <Typography className={classes.singleLotBreakdownRow} style={{ color: timeDwellingObj[timeDwellingGroups.LESS_THAN_30_DAYS].color }}>
                            {finalData[0][timeDwellingGroups.LESS_THAN_30_DAYS]} assets &lt; 30 days
                        </Typography>
                        <Typography className={classes.singleLotBreakdownRow} style={{ color: timeDwellingObj[timeDwellingGroups.MORE_THAN_30_DAYS].color }}>
                            {finalData[0][timeDwellingGroups.MORE_THAN_30_DAYS]} assets 30–60 days
                        </Typography>
                        <Typography className={classes.singleLotBreakdownRow} style={{ color: timeDwellingObj[timeDwellingGroups.MORE_THAN_60_DAYS].color }}>
                            {finalData[0][timeDwellingGroups.MORE_THAN_60_DAYS]} assets &gt; 60 days
                        </Typography>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={finalData}
                            margin={{ top: 30, right: 50, left: 10, bottom: 5 }}
                            barCategoryGap="20%"
                            barGap={6}
                        >
                            <XAxis
                                type="category"
                                axisLine={false}
                                interval={~~(finalData?.length / 40)}
                                dataKey="lotName"
                                tickLine={false}
                                tick={{
                                    color: theme.palette.primary.main,
                                    fontSize: '0.8em',
                                    fontWeight: '500',
                                    dx: -4,
                                }}
                                angle={-90}
                                textAnchor="end"
                                height={50}
                            />
                            <YAxis
                                type="number"
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(value) => Math.round(value) == value ? value : ''}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F3F5F6' }} />
                            <Legend
                                iconType="circle"
                                layout="horizontal"
                                verticalAlign="bottom"
                                iconSize={8}
                                wrapperStyle={{ bottom: 0, marginLeft: '1rem', fontSize: 11, backgroundColor: "white" }}
                                formatter={(value) => timeDwellingObj[value]?.name}
                            />
                            {timeDwelling.map((item, index) => (
                                <Bar
                                    key={index}
                                    dataKey={item.id}
                                    stackId="dwell"
                                    fill={item.color}
                                    className={classes.bar}
                                    maxBarSize={12}
                                >
                                </Bar>
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </Grid>
        </Grid>
    );
};
export default DwellWidget;
